import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import type { MeetingReview, ReviewInquiry, ReviewItem, ReviewVerdict } from "./meetingReview.types";
import { watchMeetingReview } from "./meetingReviewLive";

/** The slice of the RAW Feathers service the actions call. */
interface RawService {
  patch: (id: number, data: object, params?: object) => Promise<unknown>;
}

export type ReviewLoadState = "idle" | "loading" | "error" | "forbidden" | "ready";

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;
const codeOf = (err: unknown): number | null => {
  const c = (err as { code?: unknown } | null)?.code;
  return typeof c === "number" ? c : null;
};

/**
 * Meeting Detail → Review: the review of one processed meeting
 * (plan/2026-10-21_FROM-GIRDER_meeting-review-and-cards.md §2).
 *
 * READS go through the `meeting-reviews` ServiceStore (meetingIntelligence.stores.ts),
 * keyed by the meeting's id: this store keeps only that id and resolves the
 * review from the cache, so no API entity lives here.
 *
 * RESPONSES go through the RAW Feathers client, as the Inquiry Queue's actions
 * do: the API answers with an envelope, and the raw client rejects every
 * refusal, so a 403 or 409 can never read as success. After a response the
 * review is re-read through the ServiceStore.
 *
 * Nothing here decides what counts: whose response decides, and what the
 * close will do, come from the server (`callerDecides`, `decision`).
 *
 * LIVE (#3820): while the page is open (`subscribe`, from the route), a
 * colleague's response, the window opening or closing, and the Meeting
 * Intelligence events about this meeting each re-read the review — this
 * person's own view, through the same `load` (meetingReviewLive.ts). No
 * review ever arrives over the socket.
 *
 * SIGN-OUT (stores/sessionGuard.ts): a call in flight when the person signs
 * out rejects with SessionEndedError, and every tail after an await checks a
 * session mark, so nothing the last person did lands in the next person's
 * screen. `reset()` (purgeUserData) returns every field to its constructed
 * value.
 */
class MeetingReviewStore {
  rootStore: RootStore;

  meetingId: number | null = null;
  loading = false;
  error: string | null = null;
  forbidden = false;
  /** Which item / question a response is being sent for, so only its buttons wait. */
  busyKey: string | null = null;
  actionError: string | null = null;
  notice: string | null = null;
  /** The picked options of a question, before they are sent. */
  picks: Record<number, string[]> = {};
  /** A note to send with the next response, per item. */
  notes: Record<number, string> = {};

  private loadSeq = 0;
  /** The live subscription (plumbing, not state). */
  private stopLive: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "stopLive">(this, { rootStore: false, stopLive: false });
  }

  // ---- services ------------------------------------------------------------

  private get service(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      meetingReviewsStore?: unknown;
    };
    return ((rs.serviceStoreManager?.getStore?.("meeting-reviews") ?? rs.meetingReviewsStore) as ServiceStoreType | null | undefined) ?? null;
  }

  private get raw(): RawService | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawService } }).app;
    return app?.service("meeting-reviews") ?? null;
  }

  // ---- derived -------------------------------------------------------------

  get review(): MeetingReview | null {
    if (this.meetingId == null) return null;
    const rows = (this.service?.entities ?? []) as unknown as MeetingReview[];
    return rows.find((r) => r.id === this.meetingId) ?? null;
  }

  get state(): ReviewLoadState {
    if (this.forbidden) return "forbidden";
    if (this.error && !this.review) return "error";
    if (this.review) return "ready";
    return this.loading ? "loading" : "idle";
  }

  /** Items still open to a response first, then decided ones; stable by id within each. */
  get items(): ReviewItem[] {
    const items = this.review?.items ?? [];
    return [...items].sort((a, b) => Number(a.outcome != null) - Number(b.outcome != null) || a.id - b.id);
  }

  get openQuestions(): ReviewInquiry[] {
    return (this.review?.inquiries ?? []).filter((q) => q.stage === "internal" || q.stage === "released");
  }

  get answeredQuestions(): ReviewInquiry[] {
    return (this.review?.inquiries ?? []).filter((q) => q.stage === "answered");
  }

  /** The assignments of the review's items, as strings — what an assignment event is matched against. */
  get assignmentIds(): ReadonlySet<string> {
    return new Set(
      (this.review?.items ?? []).filter((i) => i.assignmentId != null).map((i) => String(i.assignmentId))
    );
  }

  // ---- live ----------------------------------------------------------------

  /** Follow the open review (route onEnter). Idempotent; ended by `dispose`. */
  subscribe() {
    if (this.stopLive) return;
    this.stopLive = watchMeetingReview(
      this.rootStore,
      () => ({ meetingId: this.meetingId, assignmentIds: this.assignmentIds, mayOpen: !this.forbidden }),
      () => void this.load()
    );
  }

  // ---- loading -------------------------------------------------------------

  setMeetingId(id: number | string | null) {
    const n = Number(id);
    const next = Number.isInteger(n) && n > 0 ? n : null;
    if (next === this.meetingId) return;
    this.meetingId = next;
    this.error = null;
    this.forbidden = false;
    this.actionError = null;
    this.notice = null;
    this.picks = {};
    this.notes = {};
  }

  async load() {
    const ended = sessionMark(this.rootStore);
    const seq = ++this.loadSeq;
    const id = this.meetingId;
    const service = this.service;
    if (id == null) return;
    this.loading = true;
    this.error = null;
    if (!service) {
      this.loading = false;
      this.error = "The meeting-reviews service is not available in this build.";
      return;
    }
    try {
      await service.get(id, { throwOnError: true } as never);
      if (ended()) return;
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        this.forbidden = false;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      if (ended() || seq !== this.loadSeq) return;
      runInAction(() => {
        this.forbidden = codeOf(err) === 403;
        this.error = messageOf(err, "Failed to load this meeting's review");
      });
    } finally {
      // Signed out meanwhile: reset() already put `loading` back.
      if (!ended() && seq === this.loadSeq) runInAction(() => (this.loading = false));
    }
  }

  // ---- responding ----------------------------------------------------------

  clearMessages() {
    this.actionError = null;
    this.notice = null;
  }

  setNote(itemId: number, text: string) {
    this.notes = { ...this.notes, [itemId]: text };
  }

  togglePick(inquiry: ReviewInquiry, optionId: string) {
    const current = this.picks[inquiry.id] ?? [];
    const has = current.includes(optionId);
    const next = has ? current.filter((o) => o !== optionId) : inquiry.multi ? [...current, optionId] : [optionId];
    this.picks = { ...this.picks, [inquiry.id]: next };
  }

  async respond(item: ReviewItem, verdict: ReviewVerdict) {
    const ended = sessionMark(this.rootStore);
    const note = (this.notes[item.id] ?? "").trim();
    const ok = await this.send(`item:${item.id}`, {
      action: "respond",
      actionItemId: item.id,
      verdict,
      ...(note ? { text: note } : {}),
    });
    if (ended()) return;
    if (ok) runInAction(() => (this.notes = { ...this.notes, [item.id]: "" }));
  }

  async answer(inquiry: ReviewInquiry) {
    const optionIds = this.picks[inquiry.id] ?? [];
    if (!optionIds.length) {
      this.actionError = "Pick an option first.";
      return;
    }
    const ended = sessionMark(this.rootStore);
    const ok = await this.send(`inquiry:${inquiry.id}`, {
      action: "answer",
      clientInquiryId: inquiry.id,
      optionIds,
      expect: { stage: inquiry.stage, updatedAt: inquiry.updatedAt },
    });
    if (ended()) return;
    if (ok) runInAction(() => (this.picks = { ...this.picks, [inquiry.id]: [] }));
  }

  private async send(key: string, body: Record<string, unknown>): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    const id = this.meetingId;
    const raw = this.raw;
    if (id == null || !raw) return false;
    this.busyKey = key;
    this.actionError = null;
    this.notice = null;
    try {
      const result = (await raw.patch(id, body)) as { settled?: boolean; detail?: string } | null;
      if (ended()) return false;
      runInAction(() => {
        if (body.action === "answer") {
          this.notice = result?.settled
            ? "Answered. The work it held is released to its owner."
            : result?.detail
              ? `Your answer is recorded as advice; it did not answer the question: ${result.detail}`
              : "Your answer is recorded. A maintainer of the module decides the question.";
        }
      });
      await this.load();
      if (ended()) return false;
      return true;
    } catch (err) {
      if (isSessionEnded(err)) return false;
      if (ended()) return false;
      runInAction(() => {
        this.actionError = messageOf(err, "HQ could not record that response.");
      });
      // A refusal often means the review moved (decided, answered elsewhere): show what is true now.
      await this.load();
      return false;
    } finally {
      // Signed out meanwhile: reset() cleared it, and the next person's action may own it now.
      if (!ended()) runInAction(() => (this.busyKey = null));
    }
  }

  /** Sign-out (RootStore.purgeUserData): every field back to its constructed value. */
  reset() {
    this.dispose();
    this.loadSeq = 0;
  }

  dispose() {
    // Left the page (or signed out): no re-read may land after this.
    this.stopLive?.();
    this.stopLive = null;
    this.meetingId = null;
    this.loading = false;
    this.error = null;
    this.forbidden = false;
    this.busyKey = null;
    this.actionError = null;
    this.notice = null;
    this.picks = {};
    this.notes = {};
  }
}

export default MeetingReviewStore;
